import { WidgetType } from '../landing-page/models/widget-type';
import { DATE_RANGE_PRESET_OPTIONS } from '../landing-page/widgets/filter-params';

// Per-widget-type documentation for the freeform `settings` JSON, surfaced in the
// dashboard-admin editors. Kept here (not on the DB record) since the shape is a property
// of the widget TYPE, not of an individual widget instance.
export interface WidgetSettingsExample {
  label: string;
  value: Record<string, unknown>;
}

export interface WidgetSettingsDoc {
  description: string;
  examples: WidgetSettingsExample[];
}

export const WIDGET_SETTINGS_DOCS: Record<WidgetType, WidgetSettingsDoc> = {
  [WidgetType.KeyValue]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.key_value:Label → value rows from either "record" (one record's fields) or "counts" (one or more totals) — not both.`,
    examples: [
      {
        label: 'Record source (statistics)',
        value: {
          record: {
            source: { service: 'api', path: 'statistics' },
            fields: [
              { key: 'documents_processed', label: 'Documents processed' },
              { key: 'pages_processed', label: 'Pages processed' },
              { key: 'current_month_pages', label: 'Current month pages' },
              { key: 'quota', label: 'Quota' }
            ]
          },
          filters: [
            {
              key: 'date',
              type: 'date_range',
              editable: true,
              defaultValue: 'last_30_days',
              presets: DATE_RANGE_PRESET_OPTIONS
            }
          ],
          style: '.key-value__value { color: #16a34a; font-weight: 700; }'
        }
      },
      {
        label: 'Counts source (documents) — or use count-list instead',
        value: {
          counts: [
            {
              label: 'On QC',
              source: { service: 'api', path: 'documents' },
              filters: [{ key: 'validation_step', type: 'string', defaultValue: 'qc' }]
            }
          ],
          filters: [
            {
              key: 'created_at',
              type: 'date_range',
              editable: true,
              defaultValue: 'last_30_days',
              presets: DATE_RANGE_PRESET_OPTIONS
            }
          ]
        }
      }
    ]
  },

  [WidgetType.CountList]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.count_list:One row per fetched total count, from a list endpoint.`,
    examples: [
      {
        label: 'Example',
        value: {
          filters: [
            {
              key: 'created_at',
              type: 'date_range',
              editable: true,
              defaultValue: 'last_30_days',
              presets: DATE_RANGE_PRESET_OPTIONS
            }
          ],
          items: [
            { label: 'Total documents', source: { service: 'api', path: 'documents' } },
            {
              label: 'On extraction',
              source: { service: 'api', path: 'documents' },
              filters: [{ key: 'validation_step', type: 'string', defaultValue: 'extraction' }]
            }
          ]
        }
      }
    ]
  },

  [WidgetType.LinkList]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.link_list:A list of links, each an internal route or an external URL.`,
    examples: [
      {
        label: 'Example',
        value: {
          items: [
            { label: 'Document Overview', icon: 'list', route: 'validation/overview' },
            { label: 'New Document Type', icon: 'plus', route: 'configuration/document-types' },
            { label: 'Technical Documentation', icon: 'external-link', href: 'https://docs.parashift.io' }
          ]
        }
      }
    ]
  },

  [WidgetType.StatusList]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.status_list:Live status per platform component, with an overall badge. Use "include" to pick which components to show.`,
    examples: [
      {
        label: 'Example',
        value: {
          source: { service: 'id', path: 'status_reports' },
          include: [
            'Platform: API',
            'Platform: Processing',
            'Platform: Separation',
            'Platform: Classification',
            'Platform: Extraction',
            'Platform: Automation',
            'Platform: frontend',
            'Login and Authorization services',
            'Webhooks',
            'Platform: Training'
          ]
        }
      }
    ]
  },

  [WidgetType.ContactCard]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.contact_card:A contact card: name, role, email, phone, and optional avatar.`,
    examples: [
      {
        label: 'Example',
        value: {
          name: 'Support',
          role: 'Parashift Support',
          email: 'support@parashift.io',
          phone: '+41 61 508 77 80',
          avatar: 'https://example.com/support.jpg'
        }
      }
    ]
  },

  [WidgetType.NotificationList]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.notification_list:The user's unread count and its most recent notifications.`,
    examples: [
      {
        label: 'Example',
        value: {
          source: { service: 'id', path: 'notifications' }
        }
      }
    ]
  },

  [WidgetType.PageQuota]: {
    description: $localize`:@@pdc.dashboard_admin.settings_doc.page_quota:This month's page usage against the tenant's quota, shown as a percentage.`,
    examples: [
      {
        label: 'Example',
        value: {
          source: { service: 'api', path: 'statistics' }
        }
      }
    ]
  }
};
